import { Form } from "@unform/web";
import { Ban } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Textarea from "~/components/RegisterForm/Textarea";
import { AlertContext } from "~/App";

export const InputContext = createContext({});

const REASON_OPTIONS = [
  { value: "return_from_student", label: "No Return from Student" },
  { value: "pricing_or_competition", label: "Pricing or Competition" },
  { value: "student_withdrawal", label: "Student Withdrawal" },
  { value: "postponement", label: "Postponement" },
];

export default function InactivateEnrollment({
  access,
  id,
  defaultFormType = "preview",
  selected,
  handleOpened,
}) {
  const { alertBox } = useContext(AlertContext);
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);

  const [pageData, setPageData] = useState({ loaded: false });
  const [registry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });

  const [formType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [selectedReason, setSelectedReason] = useState(null);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit(data) {
    if (!selectedReason) {
      toast("Please select an inactivation reason.", {
        type: "error",
        autoClose: 3000,
      });
      return;
    }

    const payload = {
      inactivation_reason: selectedReason,
      inactivation_notes: data.inactivation_notes || null,
    };

    try {
      await api.patch(`/enrollments/cancel-bulk`, {
        ...payload,
        enrollment_ids: selected.map((enrollment) => enrollment.id),
      });
      const count = selected.length;
      toast(count === 1 ? "Enrollment inactivated!" : `${count} enrollments inactivated!`, { autoClose: 1000 });
      setSuccessfullyUpdated(true);
      handleOpened(null);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      } else {
        toast("An error occurred. Please try again.", {
          type: "error",
          autoClose: 3000,
        });
      }
    }
  }

  useEffect(() => {
    if (!selected?.[0]?.id) return;
    async function loadEnrollment() {
      try {
        const { data } = await api.get(`/enrollments/${selected[0].id}`);
        setPageData({ ...data, loaded: true });
      } catch (err) {
        setPageData({ loaded: true });
      }
    }
    loadEnrollment();
  }, [selected?.[0]?.id]);

  const enrollmentLabel = selected?.length > 1
    ? `${selected.length} enrollments selected`
    : selected?.[0]?.fields[2] || `#${selected?.[0]?.id}`;

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Ban size={16} /> General
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={"Inactivate Enrollment - " + enrollmentLabel}
                        registry={registry}
                        InputContext={InputContext}
                      />

                      <InputLineGroup
                        title="GENERAL"
                        activeMenu={activeMenu === "general"}
                      >
                        <InputLine title="Reason">
                          <SelectPopover
                            name="inactivation_reason"
                            grow
                            required
                            title="Inactivation Reason"
                            isSearchable
                            options={REASON_OPTIONS}
                            InputContext={InputContext}
                            onChange={(el) => {
                              setSelectedReason(el?.value || null);
                              setSuccessfullyUpdated(false);
                            }}
                          />
                        </InputLine>
                        <InputLine title="Notes">
                          <Textarea
                            name="inactivation_notes"
                            title="Inactivation Notes"
                            rows={4}
                            grow
                            defaultValue=""
                            InputContext={InputContext}
                            placeholder="Optional notes about this inactivation..."
                          />
                        </InputLine>
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
